<template>
    <div class="relative w-full md:max-w-64 p-4">
        <div class="w-full text-center">
            <button
                @click="isFilterOpen = true"
                class="bg-red rounded-lg text-white font-bold p-2 md:hidden"
            >
                Filtres
            </button>
        </div>

        <div class="flex flex-wrap">
            <template v-for="(value, filter) in input" :key="filter">
                <span
                    class="flex items-center bg-blue-100 text-blue-800 text-sm font-medium rounded-full shadow-sm m-2"
                    v-if="shouldDisplayFilter(filter, value)"
                >
                    <button
                        @click="updateQuery(filter, 'all')"
                        class="ml-2 focus:outline-none text-red"
                    >
                        <span>{{ getFilterLabel(filter) }}</span>
                        <span v-if="isStockOrPromotion(filter)"></span>
                        <span v-else-if="isPriceFilter(filter)"
                            >: {{ value }} €</span
                        >
                        <span v-else
                            >: {{ getFilterValues(filter, value) }}</span
                        >
                        &times;
                    </button>
                </span>
            </template>
        </div>
        <div
            v-if="condition?.cafetaria_billing_type"
            class="border-1 border-dotted rounded-md p-4"
            :class="color == 'green' ? 'border-green' : 'border-red'"
        >
            <h3 class="font-bold text-green mb-2">
                {{
                    $translate(
                        'dashboard.fleet-manager.conditions.title_cafeteria',
                    )
                }}
            </h3>
            <div
                v-if="condition?.cafetaria_billing_type?.split(',').length"
                class="w-full"
            >
                <label
                    for="billing_type"
                    class="block text-sm font-medium text-gray-700"
                >
                    {{
                        $translate(
                            'components.dashboard.overview.bike-info.contract-type',
                        )
                    }}
                </label>
                <div class="mt-2 flex items-center space-x-2">
                    <select
                        id="billing_type"
                        v-model="input.billing_type"
                        class="disabled:opacity-75 block w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        @change="handleBillingTypeChange"
                        :disabled="
                            isSingleOption(condition?.cafetaria_billing_type)
                        "
                    >
                        <option
                            v-for="billing_type in condition?.cafetaria_billing_type?.split(
                                ',',
                            )"
                            :key="billing_type"
                            :value="billing_type"
                        >
                            {{
                                $translate(
                                    `public.shop.home.bikes.card.payements.${billing_type}`,
                                )
                            }}
                        </option>
                    </select>
                </div>
            </div>
            <div v-if="isLeasingType(input.billing_type)" class="w-full">
                <label
                    for="contract_duration"
                    class="block text-sm font-medium text-gray-700"
                >
                    {{
                        $translate(
                            'components.dashboard.overview.bike-info.duration-contract',
                        )
                    }}
                </label>
                <div class="mt-2 flex items-center space-x-2">
                    <select
                        id="contract_duration"
                        v-model="input.contract_duration"
                        class="disabled:opacity-75 block w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        @change="handleContractDurationChange"
                        :disabled="isSingleOption(condition?.contract_duration)"
                    >
                        <option
                            v-for="contract_duration in condition?.contract_duration"
                            :key="contract_duration"
                            :value="+contract_duration"
                        >
                            {{ contract_duration }}
                            {{
                                $translate(
                                    'public.shop.home.bikes.card.payements.month',
                                )
                            }}
                        </option>
                    </select>
                </div>
            </div>
            <div
                v-if="input.billing_type == 'monthly_leasing_budget'"
                class="w-full"
            >
                <label
                    for="budget_entretien"
                    class="block text-sm font-medium text-gray-700"
                    >Budget Entretien (annuel)</label
                >
                <div class="mt-2 flex items-center space-x-2">
                    <input
                        type="number"
                        id="budget_entretien"
                        v-model="input.budget_entretien"
                        class="block w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        @change="handleBudgetEntretienChange"
                    />
                </div>
                <template
                    v-if="input.budget_entretien < condition?.budget_entretien"
                >
                    <span class="text-red"
                        >Le budget entretien doit être supérieur à
                        {{ condition?.budget_entretien }} €</span
                    >
                </template>
            </div>
        </div>
        <div class="hidden md:block">
            <div class="mb-2">
                <div class="flex hidden">
                    <input
                        type="checkbox"
                        id="promotion"
                        value="true"
                        class="form-check-input"
                        :checked="!input.promotion.includes('all')"
                        @change="
                            updateQuery('promotion', $event.target.checked)
                        "
                    />
                    <label for="promotion"> En promotion</label>
                </div>
            </div>
            <FilterSection
                v-for="section in filterSections"
                :key="section.key"
                :section="section"
                :input="input"
                :open="open"
                :color="color"
                @updateQuery="updateQuery"
                @toggleOpen="handleToggleOpen"
            />
            <div
                class="mb-2 border-1 rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-2 bg-white"
                :class="color == 'green' ? 'border-green' : 'border-red'"
            >
                <div class="max-w-24">
                    <label for="min-price">{{
                        $translate('public.shop.home.bikes.label.min')
                    }}</label>
                    <input
                        type="number"
                        id="min-price"
                        name="min-price"
                        v-model="input.minPrice"
                        @change="handlePriceChange('minPrice')"
                    />
                </div>
                <div class="max-w-24">
                    <label for="max-price">{{
                        $translate('public.shop.home.bikes.label.max')
                    }}</label>
                    <input
                        type="number"
                        id="max-price"
                        v-model="input.maxPrice"
                        @change="handlePriceChange('maxPrice')"
                    />
                </div>
            </div>
        </div>
        <!-- Mobile Filter Panel -->
        <div
            v-if="isFilterOpen"
            class="fixed inset-0 z-50 bg-white w-full h-full md:hidden"
        >
            <button
                @click="isFilterOpen = false"
                class="absolute top-4 right-4 text-red-500 text-xl"
            >
                &times;
            </button>
            <div class="p-4">
                <FilterSection
                    v-for="section in filterSections"
                    :key="section.key"
                    :section="section"
                    :input="input"
                    :open="open"
                    @updateQuery="updateQuery"
                    @toggleOpen="handleToggleOpen"
                />
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import FilterSection from './FilterSection.vue';
const { t } = useI18n();

const route = useRoute();
const $router = useRouter();
const isFilterOpen = ref(false);

const emits = defineEmits(['update', 'isLoading']);

const props = defineProps({
    sizes: { type: Object, default: () => ({ UNIQUE: 0, XS: 0 }) },
    brands: { type: Object, default: () => ({ Conway: 0, Victoria: 0 }) },
    utilisations: { type: Object, default: () => ({ 'Ville et chemin': 0 }) },
    frame_types: { type: Object, default: () => ({ F: 0, H: 0 }) },
    electrics: { type: Object, default: () => ({ Y: 0, N: 0 }) },
    stockHidden: { type: Boolean, default: false },
    motorHidden: { type: Boolean, default: false },
    batteryHidden: { type: Boolean, default: false },
    condition: { type: Object, default: () => ({}) },
    color: { type: String, default: 'red' },
});

const getQueryArray = key =>
    route.query[key]
        ? route.query[key].split(',').map(decodeURIComponent)
        : ['all'];

const getContractDuration = () => {
    if (route.query.contract_duration) return route.query.contract_duration;
    if (props.condition?.contract_duration?.includes(36)) return 36;
    return props.condition?.contract_duration?.length > 0
        ? props.condition?.contract_duration[0]
        : null;
};

const getInitialState = () => ({
    utilisation: getQueryArray('utilisation'),
    brand: getQueryArray('brand'),
    electric: getQueryArray('electric'),
    size: getQueryArray('size'),
    stock: getQueryArray('stock'),
    frame_type: getQueryArray('frame_type'),
    engine: getQueryArray('engine'),
    battery: getQueryArray('battery'),
    minPrice: route.query.minPrice ?? null,
    maxPrice: route.query.maxPrice ?? null,
    billing_type: route.query.billing_type ?? null,
    budget_entretien:
        route.query.budget_entretien ??
        props.condition?.budget_entretien ??
        null,
    contract_duration: getContractDuration(),
    promotion: route.query.promotion ? [route.query.promotion] : ['all'],
});

const search = ref(route.query.search ? route.query.search : '');
const input = ref(getInitialState());
const open = ref({
    utilisation: false,
    size: false,
    brand: false,
    model: false,
    engine: false,
    battery: false,
    filters: false,
    sort: false,
});

const filterSections = ref([
    {
        key: 'utilisation',
        label: 'public.shop.home.bikes.label.use',
        items: props.utilisations,
        count: true,
    },
    {
        key: 'brand',
        label: 'public.shop.home.bikes.label.brand',
        items: Object.entries(props.brands)
            .sort((a, b) => a[0].localeCompare(b[0])) // Sort by brand name
            .reduce((obj, [key, value]) => ({ ...obj, [key]: value }), {}),

        count: true,
    },
    {
        key: 'electric',
        label: 'public.shop.home.bikes.label.electric',
        items: props.electrics,
        count: true,
    },
    {
        key: 'size',
        label: 'public.shop.home.bikes.label.size',
        items: props.sizes,
        count: true,
    },
    {
        key: 'stock',
        label: 'public.shop.home.bikes.label.stock',
        transalateKey: 'public.shop.home.bikes.stock-option',
        items: { all: 'all', true: 'in-stock' },
        hidden: props.stockHidden,
    },
    {
        key: 'frame_type',
        label: 'public.shop.home.bikes.label.frame',
        transalateKey: 'public.shop.home.bikes.frame-option',
        items: props.frame_types,
        count: true,
    },
    {
        key: 'engine',
        label: 'public.shop.home.bikes.label.engine',
        transalateKey: 'public.shop.home.bikes.engine-option',
        items: { low: '1', middle: '2', high: '3' },
        hidden: props.motorHidden && !input.value.electric.includes('Y'),
    },
    {
        key: 'battery',
        label: 'public.shop.home.bikes.label.battery',
        transalateKey: 'public.shop.home.bikes.battery-option',
        items: { low: '1', middle: '2', high: '3' },
        hidden: props.batteryHidden && !input.value.electric.includes('Y'),
    },
]);

// Watch all relevant props and update `filterSections`
const updateFilterSections = () => {
    input.value = getInitialState();
    filterSections.value = [
        {
            key: 'utilisation',
            label: 'public.shop.home.bikes.label.use',
            items: props.utilisations,
            count: true,
        },
        {
            key: 'brand',
            label: 'public.shop.home.bikes.label.brand',
            items: props.brands,
            count: true,
        },
        {
            key: 'electric',
            label: 'public.shop.home.bikes.label.electric',
            items: props.electrics,
            count: true,
        },
        {
            key: 'size',
            label: 'public.shop.home.bikes.label.size',
            items: props.sizes,
            count: true,
        },
        {
            key: 'stock',
            label: 'public.shop.home.bikes.label.stock',
            transalateKey: 'public.shop.home.bikes.stock-option',
            items: { all: 'all', true: 'in-stock' },
            hidden: props.stockHidden,
        },
        {
            key: 'frame_type',
            label: 'public.shop.home.bikes.label.frame',
            transalateKey: 'public.shop.home.bikes.frame-option',
            items: props.frame_types,
            count: true,
        },
        {
            key: 'engine',
            label: 'public.shop.home.bikes.label.engine',
            transalateKey: 'public.shop.home.bikes.engine-option',
            items: { low: '1', middle: '2', high: '3' },
            hidden: props.motorHidden && !input.value.electric.includes('Y'),
        },
        {
            key: 'battery',
            label: 'public.shop.home.bikes.label.battery',
            transalateKey: 'public.shop.home.bikes.battery-option',
            items: { low: '1', middle: '2', high: '3' },
            hidden: props.batteryHidden && !input.value.electric.includes('Y'),
        },
    ];
};

// Watch for changes in all props
watch(
    () => [
        props.utilisations,
        props.brands,
        props.electrics,
        props.sizes,
        props.stockHidden,
        props.frame_types,
        props.motorHidden,
        props.batteryHidden,
    ],
    updateFilterSections,
    { deep: true },
);
const getBikes = async () => {
    emits('isLoading', input.value, search.value);
};

const shouldDisplayFilter = (filter, value) =>
    value &&
    [
        'utilisation',
        'brand',
        'electric',
        'size',
        'frame_type',
        'engine',
        'battery',
        'stock',
        'minPrice',
        'maxPrice',
        'promotion',
    ].includes(filter) &&
    value[0] != 'all';
const getFilterLabel = filter =>
    t(`public.shop.home.bikes.card.filters.${filter}`);
const isStockOrPromotion = filter => filter == 'stock' || filter == 'promotion';
const isPriceFilter = filter => ['minPrice', 'maxPrice'].includes(filter);
const getFilterValues = (filter, value) =>
    value
        .map(v => {
            if (filter == 'utilisation')
                return t(
                    `public.shop.home.slider.card.${decodeURIComponent(v)
                        .replace(/\s+/g, '-')
                        .toLowerCase()}`,
                );
            if (v == 1 || v == 'Y')
                return t('public.pro.questionnaire.generic.form.oui');
            if (v == 0 || v == 'N')
                return t('public.pro.questionnaire.generic.form.non');
            return decodeURIComponent(v);
        })
        .join(', ');

const handleBillingTypeChange = () => {
    $router.replace({
        query: {
            ...route.query,
            billing_type: input.value.billing_type,
            budget_entretien: input.value.budget_entretien,
            contract_duration: input.value.contract_duration,
        },
    });
    getBikes();
};

const handleContractDurationChange = () => {
    $router.replace({
        query: {
            ...route.query,
            contract_duration: input.value.contract_duration,
        },
    });
    getBikes();
};

const handleBudgetEntretienChange = () => {
    $router.replace({
        query: {
            ...route.query,
            budget_entretien: input.value.budget_entretien,
        },
    });
    getBikes();
};

const handlePriceChange = key => {
    $router.replace({ query: { ...route.query, [key]: input.value[key] } });
    getBikes();
};

const isLeasingType = billing_type =>
    [
        'monthly_leasing',
        'annual_leasing',
        'pre_financed_leasing',
        'monthly_leasing_budget',
    ].includes(billing_type);
const isSingleOption = options => {
    console.log(options.length);
    return options && options.length == 1;
};

const updateQuery = (key, value) => {
    if (value === 'all' || value == '') {
        input.value[key] =
            key === 'minPrice' || key === 'maxPrice' ? null : ['all'];
    } else {
        input.value[key] = input.value[key].filter(c => c !== 'all');
        input.value[key] = input.value[key].includes(value)
            ? input.value[key].filter(c => c !== value)
            : [...input.value[key], value];
        if (input.value[key].length === 0) input.value[key] = ['all'];
    }

    const query = Object.keys(input.value).reduce((acc, key) => {
        acc[key] = Array.isArray(input.value[key])
            ? input.value[key].map(encodeURIComponent).join(',')
            : input.value[key];
        return acc;
    }, {});

    $router.replace({ query });
    getBikes();
};

const handleToggleOpen = key => {
    open.value[key] = !open.value[key];
};

if (!input.value.billing_type && props.condition?.cafetaria_billing_type) {
    input.value.billing_type =
        props.condition?.cafetaria_billing_type.split(',')[0];
    const updatedQuery = {
        ...route.query,
        billing_type: input.value.billing_type,
    };
    if (isLeasingType(input.value.billing_type))
        updatedQuery.contract_duration = input.value.contract_duration;
    if (input.value.billing_type === 'monthly_leasing_budget')
        updatedQuery.budget_entretien = input.value.budget_entretien;
    $router.replace({ query: updatedQuery });
}
</script>
